import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  apiStartQuoteVinLookup,
  apiGetQuoteVinLookup,
  type VinLookupResult,
} from 'api/quote-marketplace';

const VIN_POLL_INTERVAL = 3000;

export type VinLookupStatus =
  | 'idle'
  | 'loading'
  | 'polling'
  | 'found'
  | 'multiple_results'
  | 'error'
  | 'not_found';

interface UseVinLookupOptions {
  quoteId: number;
  vehicleId: number;
  currentVin: string;
  initialResult?: VinLookupResult;
  onResult?: (result: VinLookupResult) => void;
}

interface UseVinLookupReturn {
  result: VinLookupResult | null;
  status: VinLookupStatus;
  isLoading: boolean;
  isPolling: boolean;
  triggerLookup: (vin: string) => Promise<VinLookupResult | null>;
  clear: () => void;
}

const isTerminalStatus = (status: string | undefined): boolean =>
  status === 'found' ||
  status === 'multiple_results' ||
  status === 'error' ||
  status === 'not_found';

export const useVinLookup = ({
  quoteId,
  vehicleId,
  currentVin,
  initialResult,
  onResult,
}: UseVinLookupOptions): UseVinLookupReturn => {
  const queryClient = useQueryClient();
  const queryKey = ['vinLookup', quoteId, vehicleId];
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  const shouldResumePending =
    initialResult?.status === 'pending' &&
    initialResult.vin === currentVin &&
    currentVin.length === 17;

  const [pendingVin, setPendingVin] = useState<string | null>(
    shouldResumePending ? initialResult.vin : null,
  );

  const { data: pollResult } = useQuery({
    queryKey: [...queryKey, pendingVin],
    queryFn: () => apiGetQuoteVinLookup(quoteId, pendingVin!),
    enabled: !!pendingVin,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && isTerminalStatus(data.status)) {
        return false;
      }
      return VIN_POLL_INTERVAL;
    },
    staleTime: 0,
    gcTime: 1000 * 60 * 30,
  });

  useEffect(() => {
    if (pollResult && isTerminalStatus(pollResult.status)) {
      setPendingVin(null);
      queryClient.setQueryData([...queryKey, pollResult.vin], pollResult);
      onResultRef.current?.(pollResult);
    }
  }, [pollResult, queryClient, queryKey]);

  const startMutation = useMutation({
    mutationFn: ({ vin }: { vin: string }) =>
      apiStartQuoteVinLookup(quoteId, vin),
  });

  const triggerLookup = useCallback(
    async (vin: string): Promise<VinLookupResult | null> => {
      if (vin.length !== 17) return null;

      const cachedResult = queryClient.getQueryData<VinLookupResult>([
        ...queryKey,
        vin,
      ]);
      if (cachedResult?.vin === vin && isTerminalStatus(cachedResult.status)) {
        onResultRef.current?.(cachedResult);
        return cachedResult;
      }

      try {
        const result = await startMutation.mutateAsync({ vin });

        if (result.status === 'pending') {
          setPendingVin(vin);
          onResultRef.current?.(result);
        } else {
          queryClient.setQueryData([...queryKey, vin], result);
          onResultRef.current?.(result);
        }

        return result;
      } catch {
        const errorResult: VinLookupResult = {
          vin,
          status: 'error',
          error: 'VIN lookup failed. Please try again.',
        };
        onResultRef.current?.(errorResult);
        return errorResult;
      }
    },
    [queryClient, queryKey, startMutation],
  );

  const clear = useCallback(() => {
    setPendingVin(null);
  }, []);

  const getCachedResult = (): VinLookupResult | null => {
    if (pendingVin && pollResult) {
      return pollResult;
    }
    if (initialResult && isTerminalStatus(initialResult.status)) {
      return initialResult;
    }
    return null;
  };

  const result = getCachedResult();

  const getStatus = (): VinLookupStatus => {
    if (startMutation.isPending) return 'loading';
    if (pendingVin) return 'polling';
    if (!result) return 'idle';
    if (result.status === 'found') return 'found';
    if (result.status === 'multiple_results') return 'multiple_results';
    if (result.status === 'error') return 'error';
    if (result.status === 'not_found') return 'not_found';
    return 'idle';
  };

  const status = getStatus();

  return {
    result,
    status,
    isLoading: startMutation.isPending,
    isPolling: !!pendingVin,
    triggerLookup,
    clear,
  };
};
